import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import type { KdsTicketItem, KdsTicket, KdsItemStatus } from "@/types/kds";

interface OrderData {
  id: string;
  order_number: number;
  order_type: string;
  table_number: string | null;
  tab_number: string | null;
  customer_name: string | null;
  waiter_name: string | null;
}

function deriveTicketStatus(items: KdsTicketItem[]): KdsItemStatus {
  const nonCancelled = items.filter(i => i.status !== 'cancelled');
  if (nonCancelled.length === 0) return 'cancelled';
  if (nonCancelled.every(i => i.status === 'done')) return 'done';
  if (nonCancelled.some(i => i.status === 'in_progress')) return 'in_progress';
  if (nonCancelled.some(i => i.status === 'recalled')) return 'in_progress';
  return 'new';
}

export function useKdsTickets(productionLocationId: string | null) {
  const [tickets, setTickets] = useState<KdsTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { activeRestaurant } = useRestaurantContext();
  const ordersCache = useRef<Map<string, OrderData>>(new Map());

  const fetchOrderData = useCallback(async (orderIds: string[]): Promise<Map<string, OrderData>> => {
    const uncached = orderIds.filter(id => !ordersCache.current.has(id));
    if (uncached.length > 0) {
      const { data } = await supabase
        .from("orders")
        .select("id, order_number, order_type, table_number, tab_number, customer_name, waiter_name")
        .in("id", uncached);
      if (data) {
        for (const o of data) {
          ordersCache.current.set(o.id, o as OrderData);
        }
      }
    }
    return ordersCache.current;
  }, []);

  const fetchTickets = useCallback(async () => {
    if (!activeRestaurant || !productionLocationId) {
      setTickets([]);
      setIsLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("kds_ticket_items")
      .select("*")
      .eq("restaurant_id", activeRestaurant.id)
      .eq("production_location_id", productionLocationId)
      .in("status", ["new", "in_progress", "recalled", "done", "cancelled"])
      .order("opened_at", { ascending: true });

    if (error) {
      console.error("[KDS] Error fetching tickets:", error);
      setIsLoading(false);
      return;
    }

    const items = (data || []) as KdsTicketItem[];
    
    // Filter out done items older than 10 minutes, cancelled older than 5 minutes
    const tenMinAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    const activeItems = items.filter(i => {
      if (i.status === 'done') return i.done_at && i.done_at > tenMinAgo;
      if (i.status === 'cancelled') return i.done_at && i.done_at > fiveMinAgo;
      return true;
    });

    // Get order data
    const orderIds = [...new Set(activeItems.map(i => i.order_id))];
    await fetchOrderData(orderIds);

    // Group by order
    const grouped = new Map<string, KdsTicketItem[]>();
    for (const item of activeItems) {
      if (!grouped.has(item.order_id)) grouped.set(item.order_id, []);
      grouped.get(item.order_id)!.push(item);
    }

    const result: KdsTicket[] = [];
    for (const [orderId, orderItems] of grouped) {
      const order = ordersCache.current.get(orderId);
      if (!order) continue;
      result.push({
        orderId,
        orderNumber: order.order_number,
        orderType: order.order_type,
        tableNumber: order.table_number,
        tabNumber: order.tab_number,
        customerName: order.customer_name,
        waiterName: order.waiter_name,
        items: orderItems,
        oldestOpenedAt: orderItems.reduce((min, i) => i.opened_at < min ? i.opened_at : min, orderItems[0].opened_at),
        status: deriveTicketStatus(orderItems),
      });
    }

    // Sort: new first, then in_progress, done last; within same status by oldest
    const statusOrder: Record<KdsItemStatus, number> = { new: 0, in_progress: 1, recalled: 1, cancelled: 1, done: 2 };
    result.sort((a, b) => {
      const sd = statusOrder[a.status] - statusOrder[b.status];
      if (sd !== 0) return sd;
      return a.oldestOpenedAt.localeCompare(b.oldestOpenedAt);
    });

    setTickets(result);
    setIsLoading(false);
  }, [activeRestaurant, productionLocationId, fetchOrderData]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Realtime subscription on kds_ticket_items
  useEffect(() => {
    if (!activeRestaurant || !productionLocationId) return;

    const channel = supabase
      .channel(`kds-items-${productionLocationId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "kds_ticket_items",
          filter: `production_location_id=eq.${productionLocationId}`,
        },
        () => {
          fetchTickets();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeRestaurant, productionLocationId, fetchTickets]);

  return { tickets, isLoading, refetch: fetchTickets };
}
